import { useEffect } from "react";
import { createPortal } from "react-dom";
import { X } from "lucide-react";
import { cx } from "../../utils";

/**
 * A centred dialog for short forms. `onClose` is optional: leave it out and
 * the dialog can't be dismissed (used when a new password is required).
 */
const Modal = ({ title, description, icon, onClose, children, footer, className }) => {
  useEffect(() => {
    if (!onClose) return undefined;
    const onKey = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  return createPortal(
    <div className="fixed inset-0 z-[55] flex items-center justify-center bg-ink-900/40 p-4 backdrop-blur-sm anim-in" onClick={onClose}>
      <div role="dialog" aria-modal="true" aria-label={title} className={cx("w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl", className)} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start gap-3 px-6 pb-4 pt-6">
          {icon && <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl bg-brand-50 text-brand-600">{icon}</span>}
          <div className="min-w-0 flex-1">
            <h2 className="text-base font-semibold tracking-tight text-ink-900">{title}</h2>
            {description && <p className="mt-1 text-sm leading-relaxed text-ink-500">{description}</p>}
          </div>
          {onClose && (
            <button type="button" onClick={onClose} className="grid h-8 w-8 shrink-0 place-items-center rounded-lg text-ink-400 hover:bg-ink-100 hover:text-ink-700" aria-label="Close">
              <X size={16} />
            </button>
          )}
        </div>
        <div className="px-6 pb-5">{children}</div>
        {footer && <div className="flex items-center justify-end gap-2 border-t border-ink-100 bg-ink-50/60 px-6 py-4">{footer}</div>}
      </div>
    </div>,
    document.body,
  );
};

export default Modal;
